import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  Copy,
  Share2,
  ThumbsDown,
  ThumbsUp,
  ExternalLink,
} from "lucide-react";
import { setAssistantFeedback } from "@/api/assistant";
import { cn } from "@/cn";
import type {
  AssistantContentBlock,
  AssistantMessage,
  AssistantMessageFeedback,
} from "@ceo-cmd/shared";

type DisplayTurn = {
  key: string;
  user?: AssistantMessage;
  assistant?: AssistantMessage;
  streaming?: {
    text: string;
    tools: Array<{
      name: string;
      label?: string;
      args?: Record<string, unknown>;
      href?: string;
    }>;
    verb?: string;
    steps?: string[];
    durationMs?: number;
  };
};

function formatSeconds(ms?: number): string {
  if (!ms || ms < 0) return "a moment";
  return `${Math.max(1, Math.round(ms / 1000))} seconds`;
}

function ThoughtBlock({
  verb,
  durationMs,
  steps,
  defaultOpen,
}: {
  verb?: string;
  durationMs?: number;
  steps?: string[];
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(Boolean(defaultOpen && steps?.length));
  const label = `${verb || "Worked"} for ${formatSeconds(durationMs)}`;
  return (
    <div className="mb-2">
      <button
        type="button"
        aria-label={open ? "collapse" : "expand"}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? (
          <ChevronDown className="size-3.5" />
        ) : (
          <ChevronRight className="size-3.5" />
        )}
        {label}
      </button>
      {open && steps && steps.length > 0 && (
        <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
          {steps.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ToolCard({
  block,
}: {
  block: Extract<AssistantContentBlock, { type: "tool" }>;
}) {
  const navigate = useNavigate();
  const summary =
    typeof block.args?.text === "string"
      ? block.args.text
      : block.label || block.name;

  return (
    <button
      type="button"
      className="my-2 flex w-full max-w-xl items-start gap-3 rounded-lg border border-border bg-muted/10 px-3 py-2.5 text-left transition hover:border-brand/40 hover:bg-brand-soft/20"
      onClick={() => {
        if (block.href) navigate(block.href);
      }}
    >
      <ExternalLink className="mt-0.5 size-4 shrink-0 text-brand" />
      <div className="min-w-0 flex-1">
        <p className="line-clamp-3 text-sm text-foreground">{summary}</p>
        <p className="mt-1 text-xs font-medium text-brand">
          {block.label || block.name}
        </p>
      </div>
    </button>
  );
}

function MessageActions({
  text,
  feedback,
  onFeedback,
}: {
  text: string;
  feedback?: AssistantMessageFeedback;
  onFeedback?: (f: AssistantMessageFeedback) => void;
}) {
  return (
    <div className="mt-2 flex items-center gap-1">
      <button
        type="button"
        aria-label="Copy"
        className="rounded p-1.5 text-muted-foreground hover:bg-muted/30 hover:text-foreground"
        onClick={() => void navigator.clipboard.writeText(text)}
      >
        <Copy className="size-3.5" />
      </button>
      <button
        type="button"
        aria-label="Good response"
        className={cn(
          "rounded p-1.5 text-muted-foreground hover:bg-muted/30 hover:text-foreground",
          feedback === "good" && "text-brand",
        )}
        onClick={() => onFeedback?.(feedback === "good" ? null : "good")}
      >
        <ThumbsUp className="size-3.5" />
      </button>
      <button
        type="button"
        aria-label="Bad response"
        className={cn(
          "rounded p-1.5 text-muted-foreground hover:bg-muted/30 hover:text-foreground",
          feedback === "bad" && "text-brand",
        )}
        onClick={() => onFeedback?.(feedback === "bad" ? null : "bad")}
      >
        <ThumbsDown className="size-3.5" />
      </button>
    </div>
  );
}

function renderBlocks(blocks: AssistantContentBlock[]) {
  return blocks.map((b, i) => {
    if (b.type === "thought") {
      return (
        <ThoughtBlock
          key={i}
          verb={b.verb}
          durationMs={b.durationMs}
          steps={b.steps}
        />
      );
    }
    if (b.type === "text") {
      return (
        <div
          key={i}
          className="prose prose-sm max-w-none whitespace-pre-wrap text-foreground dark:prose-invert"
        >
          {b.text}
        </div>
      );
    }
    if (b.type === "tool") {
      return <ToolCard key={i} block={b} />;
    }
    if (b.type === "context_update") {
      return (
        <p key={i} className="text-xs text-muted-foreground">
          Context updated ({b.contextCount} items)
        </p>
      );
    }
    return null;
  });
}

function textFromBlocks(blocks: AssistantContentBlock[]): string {
  return blocks
    .filter((b): b is Extract<AssistantContentBlock, { type: "text" }> => b.type === "text")
    .map((b) => b.text)
    .join("\n");
}

export function AssistantThreadView({
  threadId,
  messages,
  streaming,
  onMessagesChange,
}: {
  threadId: string;
  messages: AssistantMessage[];
  streaming?: DisplayTurn["streaming"] | null;
  onMessagesChange?: (messages: AssistantMessage[]) => void;
}) {
  const turns = useMemo(() => {
    const result: DisplayTurn[] = [];
    let pendingUser: AssistantMessage | undefined;
    for (const m of messages) {
      if (m.role === "user") {
        if (pendingUser) {
          result.push({ key: pendingUser.messageId, user: pendingUser });
        }
        pendingUser = m;
      } else {
        result.push({
          key: m.messageId,
          user: pendingUser,
          assistant: m,
        });
        pendingUser = undefined;
      }
    }
    if (pendingUser) {
      result.push({ key: pendingUser.messageId, user: pendingUser });
    }
    if (streaming) {
      result.push({
        key: "streaming",
        streaming,
      });
    }
    return result;
  }, [messages, streaming]);

  async function shareTurn(title: string) {
    const url = `${window.location.origin}/ai-assistant?thread=${threadId}`;
    const payload = `${title}\n${url}`;
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else {
        await navigator.clipboard.writeText(payload);
      }
    } catch {
      await navigator.clipboard.writeText(url);
    }
  }

  async function feedback(messageId: string, value: AssistantMessageFeedback) {
    const res = await setAssistantFeedback(threadId, messageId, value);
    onMessagesChange?.(
      messages.map((m) => (m.messageId === messageId ? res.message : m)),
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-8 px-4 py-6">
      {turns.map((turn) => {
        const userText = turn.user
          ? textFromBlocks(turn.user.blocks)
          : undefined;
        const assistantText = turn.assistant
          ? textFromBlocks(turn.assistant.blocks)
          : turn.streaming?.text;

        return (
          <div key={turn.key} className="space-y-3">
            {userText != null && (
              <div className="flex items-start justify-between gap-3">
                <p className="text-base font-medium text-foreground">
                  {userText}
                </p>
                <button
                  type="button"
                  aria-label="Share"
                  className="shrink-0 rounded p-1.5 text-muted-foreground hover:bg-muted/30"
                  onClick={() => void shareTurn(userText)}
                >
                  <Share2 className="size-3.5" />
                </button>
              </div>
            )}

            {turn.assistant && (
              <div>
                {renderBlocks(turn.assistant.blocks)}
                <MessageActions
                  text={assistantText ?? ""}
                  feedback={turn.assistant.feedback}
                  onFeedback={(f) =>
                    void feedback(turn.assistant!.messageId, f)
                  }
                />
              </div>
            )}

            {turn.streaming && (
              <div>
                {(turn.streaming.verb || turn.streaming.steps) && (
                  <ThoughtBlock
                    verb={turn.streaming.verb}
                    durationMs={turn.streaming.durationMs}
                    steps={turn.streaming.steps}
                    defaultOpen
                  />
                )}
                {turn.streaming.text && (
                  <div className="whitespace-pre-wrap text-sm text-foreground">
                    {turn.streaming.text}
                  </div>
                )}
                {turn.streaming.tools.map((t, i) => (
                  <ToolCard
                    key={i}
                    block={{
                      type: "tool",
                      name: t.name,
                      label: t.label,
                      args: t.args,
                      href: t.href,
                    }}
                  />
                ))}
                {!turn.streaming.text && turn.streaming.tools.length === 0 && (
                  <p className="text-sm text-muted-foreground">Thinking…</p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
